import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { KineticLine } from "@/components/KineticText";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583157048761-ac1dba033233?q=80&w=2000&auto=format&fit=crop";

const scrollToContato = () =>
  document.getElementById("contato")?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.35]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Navio de cruzeiro navegando em alto-mar"
          className="h-[115%] w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-navy/25" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-navy/50 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative mx-auto w-full max-w-[1400px] px-6 pb-16 pt-40 md:px-12 md:pb-24">
        <KineticLine load delay={0.35} className="mb-6">
          <span className="inline-block bg-paper/90 px-3 py-1.5 text-[0.65rem] font-medium uppercase tracking-[0.35em] text-navy md:text-xs">
            Agência de cruzeiros marítimos
          </span>
        </KineticLine>

        <h1
          data-testid="hero-title"
          className="font-serif font-light leading-[0.95] text-paper"
          style={{ fontSize: "clamp(3rem, 9vw, 7.5rem)" }}
        >
          <KineticLine load delay={0.5}>Navegue pelo</KineticLine>
          <KineticLine load delay={0.65}>
            <em className="font-normal italic text-air">extraordinário.</em>
          </KineticLine>
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-base font-light leading-relaxed text-paper/90 md:text-lg"
          >
            Viajar de navio não é sobre o destino — é sobre o caminho. A SG Turismos
            transforma o mar em memória.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/album"
              data-testid="hero-cta-album"
              className="group inline-flex items-center gap-3 bg-paper px-7 py-3.5 text-[0.7rem] font-semibold uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:bg-air"
            >
              Ver álbum
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </Link>
            <button
              onClick={scrollToContato}
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-3 border border-paper/60 px-7 py-3.5 text-[0.7rem] font-semibold uppercase tracking-[0.25em] text-paper transition-colors duration-300 hover:bg-paper/10"
            >
              Fale conosco
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 hidden items-center gap-3 md:right-12 lg:flex"
      >
        <span className="text-[0.6rem] uppercase tracking-[0.3em] text-paper/70">Role para explorar</span>
        <span className="relative h-10 w-px overflow-hidden bg-paper/30">
          <span className="scroll-hint-line absolute inset-0 bg-paper" />
        </span>
      </motion.div>
    </section>
  );
}
